import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { miToDisplay, distUnit } from "@/lib/units";
import { WhoopReadiness } from "@/components/WhoopReadiness";
import {
  Video, Users, Apple, Timer, MessageSquare, LineChart,
  Sparkles, ChevronRight, Flame, Clock, Crown, Target, CalendarClock, CalendarDays, Lock
} from "lucide-react";

const quickActions = [
  { to: "/programs", label: "Programs", icon: CalendarDays, premium: true },
  { to: "/nutrition", label: "Nutrition", icon: Apple, premium: true },
  { to: "/exercises", label: "Videos", icon: Video },
  { to: "/community", label: "Community", icon: Users },
  { to: "/timers", label: "Timers", icon: Timer },
  { to: "/coach", label: "AI Coach", icon: MessageSquare, premium: true },
  { to: "/coaching", label: "1-on-1", icon: Crown },
  { to: "/goals", label: "Goals", icon: Target },
  { to: "/progress", label: "Progress", icon: LineChart },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [tip, setTip] = useState("");
  const [targets, setTargets] = useState(null);
  const [summary, setSummary] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [goals, setGoals] = useState([]);
  const [coachWorkouts, setCoachWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const isCoachingClient = user?.subscription_tier === "coaching";

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [w, s] = await Promise.all([
          api.get("/workouts/today"),
          api.get("/progress/summary"),
        ]);
        if (!active) return;
        setToday(w.data);
        setSummary(s.data);
      } catch (e) {}
      setLoading(false);
      // async tip + targets + goals (non-blocking)
      api.get("/ai/tip").then(({ data }) => active && setTip(data.tip)).catch(() => {});
      api.get("/progress/weekly").then(({ data }) => active && setWeekly(data)).catch(() => {});
      api.get("/goals").then(({ data }) => active && setGoals(data)).catch(() => {});
      if (user?.subscription_tier === "coaching") {
        api.get("/workouts", { params: { category: "Exclusive" } })
          .then(({ data }) => active && setCoachWorkouts(data)).catch(() => {});
      }
      if (user?.age && user?.height_in && user?.weight_lb) {
        api.post("/nutrition/calculate", {
          age: user.age, sex: user.sex || "male", height_in: user.height_in,
          weight_lb: user.weight_lb, activity_level: "moderate", goal: user.goal || "maintain", rate: "moderate",
        }).then(({ data }) => active && setTargets(data)).catch(() => {});
      }
    })();
    return () => { active = false; };
  }, [user]);

  const status = user?.access?.status;

  return (
    <AppShell
      right={
        <Badge onClick={() => navigate("/subscribe")} className="cursor-pointer bg-secondary border border-border text-[#ccff0c]">
          {status === "active" ? (user?.plan === "admin" ? "Admin" : "Active") : status === "trialing" ? "Trial" : "Expired"}
        </Badge>
      }
    >
      <div className="rd-fade-in space-y-5">
        <div>
          <p className="text-muted-foreground text-sm">Welcome back,</p>
          <h1 className="font-display text-3xl font-bold">{user?.name || "Athlete"}</h1>
        </div>

        {/* WHOOP readiness (only shows if connected) */}
        <WhoopReadiness />

        {/* Today's workout */}
        {loading ? (
          <Skeleton className="h-40 w-full rounded-2xl bg-card" />
        ) : today ? (
          <Card data-testid="today-workout-card" className="rounded-2xl bg-card border-border p-5 overflow-hidden relative">
            <div className="absolute inset-0 rd-hero-sheen opacity-60 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-[#ccff0c] text-black">Today</Badge>
                <Badge className="bg-secondary border border-border text-muted-foreground">{today.category}</Badge>
              </div>
              <h2 className="font-display text-2xl font-bold">{today.title}</h2>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {today.duration_min} min</span>
                <span className="flex items-center gap-1"><Flame className="h-4 w-4" /> {today.difficulty}</span>
              </div>
              <Button data-testid="start-today-workout" onClick={() => navigate(`/workouts/${today.id}`)} className="mt-4 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold">
                Start Workout
              </Button>
            </div>
          </Card>
        ) : null}

        {/* Coach Assigned — 1-on-1 coaching members only */}
        {isCoachingClient && (
          <div data-testid="coach-assigned-section">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wide text-[#ccff0c] font-semibold flex items-center gap-1.5">
                <Crown className="h-3.5 w-3.5" /> Coach Assigned
              </p>
              <button
                data-testid="coach-assigned-viewall"
                onClick={() => navigate("/workouts", { state: { cat: "Exclusive" } })}
                className="text-[#ccff0c] text-xs flex items-center hover:opacity-80"
              >
                View all <ChevronRight className="h-3 w-3" />
              </button>
            </div>
            {coachWorkouts.length === 0 ? (
              <Card className="rounded-2xl bg-[#ccff0c]/5 border-[#ccff0c]/20 p-4">
                <p className="text-sm text-muted-foreground">No exclusive workouts assigned yet. Your coach will drop personalized routines here.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {coachWorkouts.slice(0, 3).map((w) => (
                  <Card
                    key={w.id}
                    data-testid={`coach-workout-${w.id}`}
                    onClick={() => navigate(`/workouts/${w.id}`)}
                    className="rounded-2xl bg-[#ccff0c]/5 border-[#ccff0c]/20 p-4 cursor-pointer hover:border-[#ccff0c]/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-xl bg-[#ccff0c]/15 flex items-center justify-center flex-shrink-0">
                          <Lock className="h-5 w-5 text-[#ccff0c]" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-[#ccff0c] text-black text-[10px]">Exclusive</Badge>
                            <Badge className="bg-secondary border border-border text-muted-foreground text-[10px]">{w.category}</Badge>
                          </div>
                          <p className="font-display text-lg font-bold truncate mt-0.5">{w.title}</p>
                          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> {w.duration_min} min · {w.difficulty}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AI Tip */}
        <Card className="rounded-2xl bg-card border-border p-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-xl bg-[#ccff0c]/15 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-4.5 w-4.5 text-[#ccff0c]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Coach's tip</p>
              <p data-testid="ai-tip" className="text-sm text-muted-foreground mt-1">{tip || "Loading your daily tip…"}</p>
            </div>
          </div>
        </Card>

        {/* Upcoming goal countdown */}
        {goals.filter((g) => g.days_left !== null && g.days_left <= 60).slice(0, 1).map((g) => (
          <Card
            key={g.id}
            data-testid="dashboard-goal-countdown"
            onClick={() => navigate("/goals")}
            className="rounded-2xl bg-[#ccff0c]/10 border-[#ccff0c]/25 p-4 cursor-pointer hover:border-[#ccff0c]/50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-[#ccff0c]/20 flex items-center justify-center flex-shrink-0">
                  <CalendarClock className="h-5 w-5 text-[#ccff0c]" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-[#ccff0c] font-semibold">Upcoming goal</p>
                  <p className="font-semibold truncate">{g.title}</p>
                </div>
              </div>
              <div className="text-right flex-shrink-0 ml-3">
                <p className="font-mono text-2xl font-bold text-[#ccff0c] leading-none">{g.days_left === 0 ? "🔥" : g.days_left}</p>
                <p className="text-[10px] text-muted-foreground">{g.days_left === 0 ? "today" : "days left"}</p>
              </div>
            </div>
          </Card>
        ))}

        {/* Nutrition targets */}
        {targets && (
          <Card className="rounded-2xl bg-card border-border p-4" data-testid="nutrition-target-card">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Daily targets</p>
              <button onClick={() => navigate("/nutrition")} className="text-[#ccff0c] text-xs flex items-center">Adjust <ChevronRight className="h-3 w-3" /></button>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["Cals", targets.target_calories],["Protein", targets.protein_g+"g"],["Carbs", targets.carb_g+"g"],["Fat", targets.fat_g+"g"]].map(([l,v]) => (
                <div key={l}>
                  <p className="font-mono text-lg font-semibold">{v}</p>
                  <p className="text-[11px] text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* This week snapshot (lifetime totals live on Progress) */}
        {weekly && (
          <Card data-testid="week-snapshot" className="rounded-2xl bg-card border-border p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3">This week</p>
            <div className="grid grid-cols-3 gap-3">
              {[["Workouts", weekly.workouts],["Runs", weekly.runs],[distUnit(user) === "km" ? "Kilometers" : "Miles", miToDisplay(weekly.distance_mi, user).toFixed(1)]].map(([l,v]) => (
                <div key={l} className="text-center">
                  <p className="font-mono text-2xl font-bold">{v}</p>
                  <p className="text-[11px] text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Quick actions */}
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3">Explore</p>
          <div className="grid grid-cols-4 gap-3">
            {quickActions.map((a) => {
              const Icon = a.icon;
              const locked = a.premium && !(user?.access?.active || user?.role === "admin");
              return (
                <button
                  key={a.to}
                  data-testid={`quick-${a.label.toLowerCase().replace(' ','-')}`}
                  onClick={() => navigate(locked ? "/subscribe" : a.to)}
                  className="relative flex flex-col items-center gap-2 rounded-2xl bg-card border border-border p-3 hover:border-[#ccff0c]/40 transition-colors"
                >
                  {locked && (
                    <span data-testid={`quick-lock-${a.label.toLowerCase().replace(' ','-')}`} className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-secondary border border-border">
                      <Lock className="h-2.5 w-2.5 text-[#ccff0c]" />
                    </span>
                  )}
                  <Icon className={`h-5 w-5 ${locked ? "text-muted-foreground" : "text-[#ccff0c]"}`} />
                  <span className="text-[11px] text-muted-foreground">{a.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {user?.role === "admin" && (
          <Button data-testid="go-admin" onClick={() => navigate("/admin")} className="w-full bg-secondary border border-border text-foreground hover:border-[#ccff0c]/40">
            Open Admin Dashboard
          </Button>
        )}
      </div>
    </AppShell>
  );
}
